import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { LoggerService } from "../../../../shared/logger.service";
import { TasksService } from "../../services/tasks.service";
import { PersonService } from "@app/services/person.service";
import { ErrorHandlerService } from "@app/shared/error-handler.service";

interface TaskRequest {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  people: string[];
}

interface TaskResponse {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  person: any;
}

@Component({
  selector: "app-tasks-form",
  templateUrl: "./tasks-form.component.html",
  styleUrls: ["./tasks-form.component.css"],
})
export class TasksFormComponent implements OnInit {
  isSaving = false;
  formGroup: FormGroup;
  people: any[] = [];
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private personService: PersonService,
    private tasksService: TasksService,
    private toast: ToastrService,
    private logger: LoggerService,
    private errorHandlerService: ErrorHandlerService,
  ) {
    this.formGroup = this.fb.group({
      id: [""],
      title: ["", Validators.required],
      description: [""],
      startDate: [""],
      endDate: [""],
      people: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.params["id"];
    this.findPeople();

    if (id) {
      this.tasksService.findById(id).subscribe({
        next: (response: TaskResponse) => {
          this.formGroup.get("id").patchValue(id);
          this.formGroup.get("title").patchValue(response.title);
          this.formGroup.get("description").patchValue(response.description);
          this.formGroup.get("startDate").patchValue(response.startDate);
          this.formGroup.get("endDate").patchValue(response.endDate);

          this.formGroup.patchValue({
            people: [response.person.id],
          });
        },
        error: (error: Error) => {
          this.isSaving = false;
          this.handleErrors(error);
        },
      });
    }
  }

  onSubmit() {
    this.isSaving = true;
    const id = this.formGroup.get("id").value;
    const task: TaskRequest = {
      title: this.formGroup.get("title")?.value,
      description: this.formGroup.get("description")?.value,
      startDate: this.formGroup.get("startDate")?.value,
      endDate: this.formGroup.get("endDate")?.value,
      people: this.formGroup.get("people")?.value,
    };

    if (id) {
      // update
      this.updateTask(id, task);
      return;
    }

    // create
    this.createTask(task);
  }

  private updateTask(id: string, task: TaskRequest): void {
    this.tasksService.update(id, task).subscribe({
      next: () => {
        this.router.navigate(["tasks"]).then((success) => {
          if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        this.isSaving = false;
        this.toast.success(`🎉 Tarefa alterada com sucesso!`);
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.errorHandlerService.handle(error, "Erro ao salvar a Tarefa");
      },
      complete: () => {
        this.formGroup.reset();
      },
    });
  }

  private createTask(task: TaskRequest): void {
    this.tasksService.create(task).subscribe({
      next: () => {
        this.router.navigate(["tasks"]).then((success) => {
          if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        this.isSaving = false;
        this.toast.success("🎉 Tarefa salva com sucesso!");
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.errorHandlerService.handle(error, "Erro ao alterar a Tarefa");
      },
      complete: () => {
        this.formGroup.reset();
      },
    });
  }

  get managers(): any[] {
    return this.people.filter((p) => p.accessType === "Manager");
  }

  get supervisors(): any[] {
    return this.people.filter((p) => p.accessType === "Supervisor");
  }

  get users(): any[] {
    return this.people.filter((p) => p.accessType === "User");
  }

  getSelectedPeople(): string {
    const peopleIds = this.formGroup.get("people")?.value || [];
    return this.people
      .filter((person) => peopleIds.includes(person.id))
      .map((person) => person.name)
      .join(", ");
  }

  private findPeople() {
    this.personService.findAll().subscribe({
      next: (response: any[]) => {
        this.people = response.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
      },
      error: (err) => {
        this.logger.error("Erro para trazer os usuários", err);
      },
    });
  }

  private handleErrors(ex: any): void {
    if (ex.error.errors) {
      ex.error.errors.forEach((element) => {
        this.errorMessage = "Erro interno: " + element.message;
        this.successMessage = null;
        setTimeout(() => {
          this.errorMessage = null;
        }, 5000);
      });
    } else {
      this.toast.error(ex.error.message);
    }
  }
}
